/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2022 (original work) Open Assessment Technologies SA ;
 */
import { __ } from '@oat-sa-private/ui-core';
import { isItemWaitingForExternalScore } from '../../../util/testMap.js';

/**
 * Step progress helper factory
 * @param {Object} [options]
 * @param {Boolean} [options.showScore] Show score state on step or not
 * @returns {Function}
 */
export default function stepProgressHelperFactory(options) {
    const showScore = options && options.showScore;

    /**
     * Maps store data to StepProgress component's step
     * @param {Object} item - Item object from store
     * @param {Boolean} showBookmarkState
     * @param {Number} viewPosition - item position which is shown to the user
     * @returns {Object}  step data in the format of StepProgress component
     */
    return function getStep(item, showBookmarkState, viewPosition) {
        return {
            key: item.position,
            state: getStepState(item),
            icon: getStepIcon(item),
            label: viewPosition,
            ariaLabel: getStepAriaLabel(item, viewPosition)
        };
    };

    /**
     * Maps store data to step icon
     * @param {Object} item - Item object from store
     * @returns {String} icon for StepProgress step
     */
    function getStepIcon(item) {
        return item.informational ? 'info-bare-16' : null;
    }

    /**
     * Maps store data to step aria-label
     * @param {Object} item - Item object from store
     * @param {Number} position
     * @returns {String} aria-label for StepProgress step
     */
    function getStepAriaLabel(item, position) {
        if (item.informational) {
            return __('Informational Item.');
        }

        let ariaLabel = `${__('Question %d.', position)} `;

        switch (getStepState(item)) {
            case 'correct':
                ariaLabel += __('Correct.');
                break;
            case 'incorrect':
                ariaLabel += __('Incorrect.');
                break;
            case 'partial':
                ariaLabel += __('Partial.');
                break;
            case 'waiting':
                ariaLabel += __('Awaiting manual scoring.');
                break;
            case 'completed':
                ariaLabel += __('Completed.');
                break;
            default:
                ariaLabel += __('Not completed.');
                break;
        }

        return ariaLabel;
    }

    /**
     * Return review state
     * @param {Object} item - Item object from testMap
     * @returns {String|undefined} state name | default state
     */
    function getStepState(item) {
        const { maxScore, score, answered, viewed } = item;

        if (!viewed && !answered) {
            return;
        } else if (!answered) {
            return 'visited';
        }

        if (!showScore || !maxScore) {
            return 'completed';
        }

        if (maxScore) {
            if (isItemWaitingForExternalScore(item)) {
                return 'waiting';
            } else if (score === 0) {
                return 'incorrect';
            } else if (maxScore !== score) {
                return 'partial';
            } else {
                return 'correct';
            }
        }
    }
}
